import { Color, DoubleSide, MeshBasicNodeMaterial, Vector4, type Texture } from 'three/webgpu';
import {
  clamp,
  float,
  max,
  min,
  mix,
  select,
  sin,
  texture,
  time,
  uniform,
  uv,
  vec2,
  vec3,
} from 'three/tsl';
import type { Node, UniformNode } from 'three/webgpu';

import { BLOOM_THRESHOLD, COLORS, EMISSIVE_GAIN } from '../../core/constants';
import { LINE_WIDTH, type Rect } from './layout';
import { dashMask, fillMask, sdArc, sdBox, sdCircle, sdSegment, strokeMask } from './sdf';
import type { Ramp } from './waveform';

type F = Node<'float'>;
type V2 = Node<'vec2'>;

export const ON_COLOR = COLORS.teal;
export const OFF_COLOR = COLORS.lightgrey;

/** Headroom over the threshold, so a colour that just makes it blooms fully. */
const BLOOM_MARGIN = 1.15;

/** Rec. 709 luminance, which is what the bloom pass thresholds on. */
function luminance(color: Color): number {
  return 0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b;
}

/**
 * A palette colour driven just hard enough that, once the display's gain is
 * applied, it crosses the bloom threshold. Red and blue carry little luminance,
 * so they need several times the drive that teal or yellow do; a colour that
 * already makes it is left alone. The atlas is float, so the drive survives.
 */
export function bloomable(color: Color): Color {
  const needed = (BLOOM_THRESHOLD * BLOOM_MARGIN) / EMISSIVE_GAIN;
  const drive = Math.max(1, needed / luminance(color));
  return color.clone().multiplyScalar(drive);
}

type FloatUniform = UniformNode<'float', number>;
type Vec4Uniform = UniformNode<'vec4', Vector4>;
type ColorUniform = UniformNode<'color', Color>;

/** A panel material plus the uniforms the render loop pokes each frame. */
export interface PanelMaterial<U> {
  material: MeshBasicNodeMaterial;
  uniforms: U;
}

function rgb(color: Color) {
  return vec3(color.r, color.g, color.b);
}

function screenMaterial(): MeshBasicNodeMaterial {
  const material = new MeshBasicNodeMaterial();
  material.transparent = true;
  material.depthTest = false;
  material.depthWrite = false;
  material.toneMapped = false;
  // The offscreen camera is flipped vertically, which reverses winding.
  material.side = DoubleSide;
  return material;
}

/** Fragment position inside a panel, in atlas pixels. */
function pixelSpace(size: V2): V2 {
  return uv().mul(size);
}

/**
 * Distance to the buffer drawn as a polyline — the GPU counterpart of the old
 * `drawBuffer`.
 *
 * Rather than walking every sample, each fragment finds the three segments that
 * could possibly be nearest to it and takes the minimum. That is a fixed cost
 * per pixel however long the buffer is.
 */
function polylineDistance(
  map: Texture,
  options: {
    size: V2;
    sampleCount: number;
    skip: number;
    ramp?: Ramp;
    centred: boolean;
    amplitude: number;
  },
): F {
  const { size, sampleCount, skip, ramp, centred, amplitude } = options;
  const pointCount = Math.floor((sampleCount - 1) / skip) + 1;
  const p = pixelSpace(size).toVar();

  const pointAt = (index: F): V2 => {
    const j = clamp(index, 0, pointCount - 1).toVar();
    const sampleIndex = j.mul(skip).toVar();
    const t = sampleIndex.div(sampleCount);
    const raw = texture(map, vec2(sampleIndex.add(0.5).div(sampleCount), 0.5)).r;
    const shaped = ramp ? ramp(raw, t) : raw;
    const y = centred ? shaped.mul(amplitude).add(0.5) : shaped.mul(amplitude);
    return vec2(j.div(pointCount - 1).mul(size.x), y.mul(size.y));
  };

  // uv.x maps straight onto the point index, so only its neighbours matter.
  const here = p.x.div(size.x).mul(pointCount - 1).floor().toVar();

  let distance: F = float(1e9);
  for (let offset = -1; offset <= 1; offset++) {
    const index = here.add(offset).toVar();
    distance = min(distance, sdSegment(p, pointAt(index), pointAt(index.add(1))));
  }
  return distance;
}

/** One buffer under one ramp, lit when its effect is engaged. */
export function createWaveformPanel(
  map: Texture,
  options: {
    rect: Rect;
    sampleCount: number;
    skip: number;
    ramp?: Ramp;
    centred?: boolean;
    amplitude?: number;
  },
): PanelMaterial<{ active: FloatUniform }> {
  const { rect, sampleCount, skip, ramp, centred = true, amplitude = 0.5 } = options;
  const active = uniform(0);

  const distance = polylineDistance(map, {
    size: vec2(rect.width, rect.height),
    sampleCount,
    skip,
    ramp,
    centred,
    amplitude,
  });

  const material = screenMaterial();
  material.colorNode = mix(rgb(OFF_COLOR), rgb(ON_COLOR), active);
  material.opacityNode = strokeMask(distance, float(LINE_WIDTH));
  return { material, uniforms: { active } };
}

/**
 * The ADSR envelope, plus the pulsing bar marking which stage the data wheel
 * is currently editing.
 *
 * `editStage` is the quarter of the panel to highlight (0-3) or -1 for none;
 * `outerRect` is the un-inset region the bar spans.
 */
export function createEnvelopePanel(options: { rect: Rect; outerRect: Rect }): PanelMaterial<{
  adsr: Vec4Uniform;
  editStage: FloatUniform;
}> {
  const { rect, outerRect } = options;
  const adsr = uniform(new Vector4(0.0001, 1, 0.5, 1));
  const editStage = uniform(-1);

  const size = vec2(rect.width, rect.height);
  const p = pixelSpace(size).toVar();

  const quarter = float(rect.width * 0.25);
  const attackX = adsr.x.mul(quarter).toVar();
  const decayX = attackX.add(adsr.y.mul(quarter)).toVar();
  const sustainY = adsr.z.oneMinus().mul(rect.height).toVar();
  const releaseX = float(rect.width).sub(adsr.w.mul(quarter)).toVar();

  const bottomLeft = vec2(0, rect.height);
  const peak = vec2(attackX, 0);
  const sustainIn = vec2(decayX, sustainY);
  const sustainOut = vec2(releaseX, sustainY);
  const bottomRight = vec2(rect.width, rect.height);

  const distance = min(
    min(sdSegment(p, bottomLeft, peak), sdSegment(p, peak, sustainIn)),
    min(sdSegment(p, sustainIn, sustainOut), sdSegment(p, sustainOut, bottomRight)),
  );

  // The marker lives in the un-inset rect, so shift back out by the margin.
  const outerP = p.add(vec2(rect.x - outerRect.x, rect.y - outerRect.y)).toVar();
  const barHeight = float(outerRect.height * 0.12);
  const stageIndex = outerP.x.div(outerRect.width * 0.25).floor();
  const inBar = outerP.y.greaterThan(float(outerRect.height).sub(barHeight));
  const marker = select(inBar.and(stageIndex.equal(editStage)), float(1), float(0)).mul(
    sin(time.mul(6)).mul(0.25).add(0.75),
  );

  const material = screenMaterial();
  material.colorNode = mix(rgb(ON_COLOR), rgb(COLORS.pink), marker);
  material.opacityNode = max(strokeMask(distance, float(LINE_WIDTH)), marker);
  return { material, uniforms: { adsr, editStage } };
}

/** Two offset boxes joined at the corners — the reverb room diagram. */
export function createReverbPanel(rect: Rect): PanelMaterial<{ active: FloatUniform }> {
  const active = uniform(0);
  const p = pixelSpace(vec2(rect.width, rect.height)).toVar();
  const stroke = float(LINE_WIDTH * 0.5);

  const boxW = rect.width / 1.5;
  const boxH = rect.width / 2;
  const half = vec2(boxW / 2, boxH / 2);
  const outerCentre = vec2(rect.width * 0.06 + boxW / 2, rect.height * 0.14 + boxH / 2);
  const innerCentre = outerCentre.add(vec2(rect.width * 0.2, rect.height * 0.24));

  const solid = strokeMask(sdBox(p.sub(innerCentre), half).abs(), stroke);
  const dashed = strokeMask(sdBox(p.sub(outerCentre), half).abs(), stroke).mul(
    dashMask(p.x.add(p.y), stroke, float(LINE_WIDTH * 1.5)),
  );

  let rays: F = float(1e9);
  for (const [sx, sy] of [
    [-1, -1],
    [1, 1],
    [-1, 1],
    [1, -1],
  ] as const) {
    const corner = vec2(half.x.mul(sx), half.y.mul(sy));
    rays = min(rays, sdSegment(p, outerCentre.add(corner), innerCentre.add(corner)));
  }

  const material = screenMaterial();
  material.colorNode = mix(rgb(OFF_COLOR), rgb(ON_COLOR), active);
  material.opacityNode = max(max(solid, dashed), strokeMask(rays, stroke));
  return { material, uniforms: { active } };
}

/**
 * A progress ring with a pulsing disc at its centre — the record, overwrite,
 * demo and loop-playback indicators all share this.
 */
export function createProgressPanel(rect: Rect): PanelMaterial<{
  progress: FloatUniform;
  ringOpacity: FloatUniform;
  discOpacity: FloatUniform;
  ringColor: ColorUniform;
  discColor: ColorUniform;
}> {
  const progress = uniform(0);
  const ringOpacity = uniform(0);
  const discOpacity = uniform(0);
  const ringColor = uniform(new Color(COLORS.red));
  const discColor = uniform(new Color(COLORS.pink));

  const size = vec2(rect.width, rect.height);
  const radius = Math.min(rect.width, rect.height) * 0.5 - LINE_WIDTH;
  const centred = pixelSpace(size).sub(size.mul(0.5)).toVar();

  const ring = strokeMask(sdArc(centred, float(radius), progress), float(LINE_WIDTH * 0.6)).mul(
    ringOpacity,
  );
  const disc = fillMask(sdCircle(centred, float(radius * 0.66))).mul(discOpacity);

  const material = screenMaterial();
  material.colorNode = mix(ringColor, discColor, disc);
  material.opacityNode = clamp(ring.add(disc), 0, 1);
  return { material, uniforms: { progress, ringOpacity, discOpacity, ringColor, discColor } };
}

/**
 * Full-screen quad that redraws the previous frame at reduced brightness.
 *
 * The 2D display never cleared its canvas — it painted 60%-opaque black over
 * the previous frame — so the waveforms left phosphor trails behind them. This
 * reproduces that decay across the ping-ponged render targets, with the falloff
 * expressed per second so it no longer depends on the frame rate.
 */
export function createTrailMaterial(previous: Node<'vec4'>): PanelMaterial<{ decay: FloatUniform }> {
  const decay = uniform(0);
  const material = new MeshBasicNodeMaterial();
  material.depthTest = false;
  material.depthWrite = false;
  material.toneMapped = false;
  material.side = DoubleSide;
  material.colorNode = previous.rgb.mul(decay);
  material.opacityNode = previous.a.mul(decay);
  material.transparent = false;
  return { material, uniforms: { decay } };
}
